<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'
import MarkdownIt from 'markdown-it'
import { computed, nextTick, ref, useId, watch } from 'vue'

interface Props {
  value: string
  /** Collapsed height in lines; 0 renders the full value with no toggle. */
  lines?: number
  emptyLabel?: string
  /**
   * Render single newlines as line breaks. Needed for content authored before
   * the markdown editor existed, where markdown would otherwise collapse them.
   */
  preserveLineBreaks?: boolean
  /** Set false to clamp without a "Show more" toggle (e.g. inside a clickable row). */
  expandable?: boolean
  expandLabel?: string
  collapseLabel?: string
}

const emit = defineEmits<{ toggle: [expanded: boolean] }>()

const props = withDefaults(defineProps<Props>(), {
  lines: 4,
  emptyLabel: 'No description',
  preserveLineBreaks: false,
  expandable: true,
  expandLabel: 'Show more',
  collapseLabel: 'Show less',
})

const isExpanded = ref(false)
const isOverflowing = ref(false)
const contentEl = ref<HTMLElement | null>(null)
const contentId = useId()

const normalized = computed(() =>
  (props.value ?? '').replace(/###(?!\s)/g, '### '),
)

const isEmpty = computed(() => normalized.value.trim().length === 0)

// Rendered synchronously. `<MDC>` resolves its parse asynchronously, and in the
// app that never produced output — every excerpt body rendered blank. `html`
// stays off so stored content cannot inject markup, and links are not
// auto-detected.
const md = new MarkdownIt({ html: false, linkify: false, breaks: false })

// The editor serializes underline as `++text++`, which markdown-it has no rule
// of its own for. The tags come from the plugin's own tokens, so `html: false`
// still holds.
md.use(underlinePlugin)

const rendered = computed(() => md.render(normalized.value))

const isClamped = computed(() => props.lines > 0 && !isExpanded.value)

const clampStyle = computed(() =>
  isClamped.value ? { '--clm-lines': String(props.lines) } : undefined,
)

const measureOverflow = () => {
  const el = contentEl.value
  if (!el) return
  if (!isClamped.value) {
    isOverflowing.value = isExpanded.value
    return
  }
  isOverflowing.value = el.scrollHeight - el.clientHeight > 1
}

useResizeObserver(contentEl, measureOverflow)

watch(
  () => props.value,
  async () => {
    await nextTick()
    measureOverflow()
  },
  { immediate: true },
)

const showToggle = computed(
  () => props.expandable && (isOverflowing.value || isExpanded.value),
)

const toggle = () => {
  isExpanded.value = !isExpanded.value
  emit('toggle', isExpanded.value)
}
</script>

<template>
  <p v-if="isEmpty" class="m-0 text-[--ui-text-muted]">
    {{ emptyLabel }}
  </p>
  <div v-else>
    <div
      :id="contentId"
      ref="contentEl"
      :class="[
        'clm-content',
        { 'clm-clamped': isClamped, 'clm-preserve-breaks': preserveLineBreaks },
      ]"
      :style="clampStyle"
      v-html="rendered"
    />
    <button
      v-if="showToggle"
      class="text-primary-600 hover:text-primary-700 mt-1 cursor-pointer text-xs font-medium focus:outline-none focus-visible:underline"
      :aria-controls="contentId"
      :aria-expanded="isExpanded"
      type="button"
      @click="toggle"
    >
      {{ isExpanded ? collapseLabel : expandLabel }}
    </button>
  </div>
</template>

<style scoped>
.clm-clamped {
  display: -webkit-box;
  -webkit-line-clamp: var(--clm-lines, 4);
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Inherits into the rendered markdown; `pre`/`code` keep their own declared
   white-space, so only paragraph soft breaks become visible line breaks. */
.clm-preserve-breaks {
  white-space: pre-line;
}

/* Tailwind's preflight strips heading and list styling, so the rendered
   markdown needs it back. Kept tight: most call sites are clamped previews. */
.clm-content :deep(h1),
.clm-content :deep(h2),
.clm-content :deep(h3),
.clm-content :deep(h4) {
  font-weight: 600;
  line-height: 1.3;
  margin: 0.5em 0 0.25em;
}

.clm-content :deep(h1) {
  font-size: 1.25em;
}

.clm-content :deep(h2) {
  font-size: 1.15em;
}

.clm-content :deep(h3),
.clm-content :deep(h4) {
  font-size: 1.05em;
}

.clm-content :deep(p),
.clm-content :deep(ul),
.clm-content :deep(ol),
.clm-content :deep(blockquote) {
  margin: 0.35em 0;
}

.clm-content :deep(ul),
.clm-content :deep(ol) {
  padding-left: 1.25em;
}

.clm-content :deep(ul) {
  list-style: disc;
}

.clm-content :deep(ol) {
  list-style: decimal;
}

.clm-content :deep(li) {
  margin: 0.15em 0;
}

.clm-content :deep(strong) {
  font-weight: 600;
}

.clm-content :deep(em) {
  font-style: italic;
}

.clm-content :deep(u) {
  text-decoration: underline;
}

.clm-content :deep(a) {
  text-decoration: underline;
}

.clm-content :deep(blockquote) {
  border-left: 2px solid currentColor;
  opacity: 0.85;
  padding-left: 0.75em;
}

.clm-content :deep(code) {
  font-family: var(--font-mono, monospace);
  font-size: 0.9em;
}

.clm-content :deep(> :first-child) {
  margin-top: 0;
}

.clm-content :deep(> :last-child) {
  margin-bottom: 0;
}
</style>
